import { useRef } from "react";

import { convertToEnglishNumber } from "../../../utils";

interface OtpInputProps {
  value: string;
  length?: number;
  disabled?: boolean;
  onChange: (value: string) => void;
}

export const OtpInput = ({
  value,
  length = 6,
  disabled,
  onChange,
}: OtpInputProps) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (index: number, inputValue: string) => {
    const converted = convertToEnglishNumber(inputValue);

    // Some keyboards (iOS SMS autofill, Samsung, Persian IMEs) deliver the
    // whole code (or several chars) in one event - fill them in order.
    if (converted.length > 1) {
      const digits = converted.replace(/\D/g, "").slice(0, length);

      if (!digits) return;

      onChange(digits);

      const nextIndex = Math.min(digits.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    const digit = converted.replace(/\D/g, "").slice(-1);

    if (!digit) return;

    const otpArray = value.padEnd(length, "").split("");

    otpArray[index] = digit;

    const nextOtp = otpArray.join("").slice(0, length);

    onChange(nextOtp);

    if (index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key !== "Backspace") return;

    const otpArray = value.padEnd(length, "").split("");

    if (otpArray[index]) {
      otpArray[index] = "";
      onChange(otpArray.join(""));
      return;
    }

    if (index > 0) {
      otpArray[index - 1] = "";

      onChange(otpArray.join(""));

      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();

    const pastedValue = convertToEnglishNumber(
      event.clipboardData.getData("text"),
    )
      .replace(/\D/g, "")
      .slice(0, length);

    if (!pastedValue) return;

    onChange(pastedValue);

    const nextIndex = Math.min(pastedValue.length, length - 1);

    inputRefs.current[nextIndex]?.focus();
  };

  return (
    <div
      dir="ltr"
      className="flex w-full items-center justify-center gap-2 md:gap-3"
      onPaste={handlePaste}
    >
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          maxLength={1}
          value={value[index] ?? ""}
          onChange={(event) => handleChange(index, event.target.value)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          disabled={disabled}
          className="h-14 w-11 rounded-xl bg-white text-center font-kalameh text-xl font-semibold  text-gray-800 outline-none transition focus:ring-2 focus:ring-[#5abcf0] disabled:cursor-not-allowed disabled:opacity-60 md:h-15 md:w-15"
        />
      ))}
    </div>
  );
};
